import { z } from 'zod';

// The Platform's member sign-up limit for a first name or a surname
const MAX_LENGTH = 50;
// A full name: a first name, a space and a surname
const FULL_NAME_MAX_LENGTH = MAX_LENGTH * 2 + 1;

// The characters the Platform's member sign-up accepts in a name
// (perci-platform-members, `^(?! )(?!.* $)[a-zA-ZÀ-ſ '-]+$`): ASCII and
// accented Latin letters (U+00C0-U+017F), spaces, hyphens and apostrophes.
// The Platform also rejects a leading or trailing space; here the value is
// trimmed first, so that rule always holds. The empty string passes, so an
// optional name can be left blank; a required one fails on its minimum length.
const NAME_CHARACTERS = /^[a-zA-ZÀ-ſ '-]*$/;
const INVALID_CHARACTERS =
  'Must contain only letters, spaces, hyphens and apostrophes';

const tooLong = (maxLength: number) =>
  `Must be at most ${maxLength} characters long`;

const personName = (maxLength: number, requiredMessage?: string) => {
  const name = z.string(requiredMessage).trim();
  const required = requiredMessage ? name.min(1, requiredMessage) : name;

  return required
    .max(maxLength, tooLong(maxLength))
    .regex(NAME_CHARACTERS, INVALID_CHARACTERS);
};

/**
 * A person's first name or surname, held to the rule the Platform's member
 * sign-up applies, so a name taken in by the EBP (a referral's patient, for
 * one) is never rejected when the member signs up. Leading and trailing
 * spaces are trimmed from the parsed value.
 *
 * @param requiredMessage reported when the value is missing, empty or only
 * spaces; omit for an optional name (add `.optional()` at the field)
 */
export const nameValidation = (requiredMessage?: string) =>
  personName(MAX_LENGTH, requiredMessage);

/**
 * A person's full name: {@link nameValidation}'s rule, with room for a first
 * name and a surname.
 */
export const fullNameValidation = (requiredMessage?: string) =>
  personName(FULL_NAME_MAX_LENGTH, requiredMessage);
